import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AdminSidebar from "../components/AdminSidebar";

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const STAGE_ORDER = [
  "LEAD",
  "AGENT_ASSIGNED",
  "DOCUMENT_COLLECTION",
  "INITIAL_FEE_PAID",
  "SUBMITTED",
  "OFFER_RECEIVED",
  "DEPOSIT_PAID",
  "OFFICIAL_ACCEPTANCE",
  "VISA_APPLICATION",
  "CLOSED",
];

const stageLabel = (s) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function AdminApplicationDetailPage() {
  const { id } = useParams();
  const [application, setApplication] = useState(null);
  const [agents, setAgents] = useState([]);
  const [tab, setTab] = useState("personal");
  const [nextStage, setNextStage] = useState("");
  const [reassignOpen, setReassignOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [resendingPassword, setResendingPassword] = useState(false);
  const [resendMessage, setResendMessage] = useState(null); // { type: 'success' | 'error', text }

  const token = localStorage.getItem("adminToken");

  const fetchApplication = () => {
    fetch(`${API_BASE}/admin/applications/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((json) => {
        if (json.success) {
          setApplication(json.data);
          const currentIdx = STAGE_ORDER.indexOf(json.data.currentStage);
          setNextStage(STAGE_ORDER[currentIdx + 1] || json.data.currentStage);
        }
      });
  };

  useEffect(fetchApplication, [id]);

  useEffect(() => {
    fetch(`${API_BASE}/admin/agents`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((json) => setAgents(json.success ? json.data : []));
  }, []);

  const handleStageUpdate = async () => {
    await fetch(`${API_BASE}/admin/applications/${id}/stage`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ toStage: nextStage }),
    });
    fetchApplication();
  };

  const handleApprove = async () => {
    await fetch(`${API_BASE}/admin/applications/${id}/approve`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchApplication();
  };

  const handleReject = async () => {
    await fetch(`${API_BASE}/admin/applications/${id}/reject`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ closeNote: rejectNote }),
    });
    setRejectOpen(false);
    fetchApplication();
  };

  const handleReassign = async (agentId) => {
    await fetch(`${API_BASE}/admin/applications/${id}/assign`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ agentId }),
    });
    setReassignOpen(false);
    fetchApplication();
  };

  const handleVerifyDoc = async (docId, status) => {
    await fetch(
      `${API_BASE}/admin/applications/${id}/documents/${docId}/verify`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      },
    );
    fetchApplication();
  };

  const handleResendPassword = async () => {
    if (
      !window.confirm(
        `Reset ${application.applicant?.fullName}'s password and email them a new temporary one? Their current password will stop working immediately.`,
      )
    )
      return;
    setResendingPassword(true);
    setResendMessage(null);
    try {
      const res = await fetch(
        `${API_BASE}/admin/applications/${id}/resend-password`,
        { method: "PATCH", headers: { Authorization: `Bearer ${token}` } },
      );
      const json = await res.json();
      if (json.success) {
        setResendMessage({ type: "success", text: "New password emailed to the applicant." });
      } else {
        setResendMessage({ type: "error", text: json.error?.message || "Failed to resend password." });
      }
    } catch {
      setResendMessage({ type: "error", text: "Failed to resend password — check your connection and try again." });
    } finally {
      setResendingPassword(false);
    }
  };

  if (!application)
    return <p className="text-center py-40 text-[#64748b]">Loading...</p>;

  const isClosed = application.currentStage === "CLOSED";

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <AdminSidebar />
      <main className="ml-64 min-h-screen p-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <nav className="flex items-center text-sm text-[#64748b] mb-2">
              <Link className="hover:text-[#00255a]" to="/admin/applications">
                Applications
              </Link>
              <span className="material-symbols-outlined text-[16px] mx-1">
                chevron_right
              </span>
              <span className="text-[#00255a] font-bold">
                {application.applicant?.fullName}
              </span>
            </nav>
            <div className="flex items-center gap-4 flex-wrap">
              <h2 className="text-3xl font-bold text-[#00255a]">
                {application.applicant?.fullName}
              </h2>
              <span className="px-3 py-1 bg-[#8fb3ff]/20 text-[#174388] text-xs font-bold rounded-full border border-[#8fb3ff]/30 uppercase">
                {application.type}
              </span>
              <span className="px-3 py-1 bg-[#ffdad8] text-[#862026] text-xs font-bold rounded-full border border-[#ffb3b1] uppercase">
                {stageLabel(application.currentStage)}
              </span>
            </div>
            <p className="text-[#64748b] mt-1">
              {application.opportunity?.title ||
                application.formResponses?.fieldOfStudy ||
                application.formResponses?.sector ||
                "General Application"}{" "}
              ·{" "}
              {application.opportunity?.country ||
                application.formResponses?.targetCountry ||
                "—"}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleResendPassword}
              disabled={resendingPassword}
              className="flex items-center px-4 py-2 bg-white border border-[#e2e8f0] rounded-lg text-[#00255a] font-bold text-sm hover:bg-[#f2f4f6] transition-all disabled:opacity-60"
            >
              <span className="material-symbols-outlined mr-2 text-[18px]">
                password
              </span>
              {resendingPassword ? "Sending..." : "Resend Password"}
            </button>
            <button
              onClick={() => setReassignOpen(true)}
              className="flex items-center px-4 py-2 bg-white border border-[#e2e8f0] rounded-lg text-[#00255a] font-bold text-sm hover:bg-[#f2f4f6] transition-all"
            >
              <span className="material-symbols-outlined mr-2 text-[18px]">
                person_search
              </span>
              Reassign Agent
            </button>
            {!isClosed && (
              <>
                <button
                  onClick={() => setRejectOpen(true)}
                  className="flex items-center px-4 py-2 bg-white border border-red-300 text-red-600 rounded-lg font-bold text-sm hover:bg-red-50 transition-all"
                >
                  <span className="material-symbols-outlined mr-2 text-[18px]">
                    cancel
                  </span>
                  Reject
                </button>
                <button
                  onClick={handleApprove}
                  className="flex items-center px-6 py-2 text-white rounded-lg font-bold text-sm shadow-lg hover:scale-[1.02] transition-transform"
                  style={{
                    background:
                      "linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)",
                  }}
                >
                  <span className="material-symbols-outlined mr-2 text-[18px]">
                    verified_user
                  </span>
                  Approve
                </button>
              </>
            )}
          </div>
        </div>

        {resendMessage && (
          <div
            className={`mb-6 p-3 rounded-lg text-sm border ${
              resendMessage.type === "success"
                ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                : "bg-red-50 border-red-200 text-red-700"
            }`}
          >
            {resendMessage.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Main Column */}
          <div className="lg:col-span-8 space-y-6">
            <div className="bg-white rounded-xl shadow-[0px_10px_30px_rgba(30,60,114,0.05)] border border-[#e2e8f0] overflow-hidden">
              <div className="flex border-b border-[#e2e8f0] px-6">
                {["personal", "documents", "payments"].map((t) => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={`px-6 py-4 font-bold text-sm transition-all ${
                      tab === t
                        ? "text-[#00255a] border-b-2 border-[#00255a]"
                        : "text-[#64748b] hover:text-[#00255a]"
                    }`}
                  >
                    {t === "personal"
                      ? "Personal Info"
                      : t === "documents"
                        ? "Documents"
                        : "Payment Log"}
                  </button>
                ))}
              </div>

              <div className="p-8">
                {tab === "personal" && (
                  <div className="space-y-8">
                    <div>
                      <h4 className="text-xs font-bold text-[#00255a] uppercase tracking-widest mb-4">
                        Contact & Identity
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12">
                        <InfoField
                          label="Full Legal Name"
                          value={application.applicant?.fullName}
                        />
                        <InfoField
                          label="Email Address"
                          value={application.applicant?.email}
                        />
                        <InfoField
                          label="WhatsApp / Phone"
                          value={
                            application.applicant?.whatsapp ||
                            application.applicant?.phone
                          }
                        />
                        <InfoField
                          label="Nationality"
                          value={application.applicant?.nationality}
                        />
                        <InfoField
                          label="Date of Birth"
                          value={
                            application.applicant?.dateOfBirth
                              ? new Date(
                                  application.applicant.dateOfBirth,
                                ).toLocaleDateString()
                              : "—"
                          }
                        />
                        <InfoField
                          label="Gender"
                          value={application.applicant?.gender}
                        />
                        <div className="md:col-span-2">
                          <InfoField
                            label="Home Address"
                            value={application.applicant?.address}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="pt-8 border-t border-[#e2e8f0]">
                      <h4 className="text-xs font-bold text-[#00255a] uppercase tracking-widest mb-4">
                        Passport
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12">
                        <InfoField
                          label="Passport Number"
                          value={application.formResponses?.passportNumber}
                        />
                        <InfoField
                          label="Passport Expiry"
                          value={
                            application.formResponses?.passportExpiry
                              ? new Date(
                                  application.formResponses.passportExpiry,
                                ).toLocaleDateString()
                              : "—"
                          }
                        />
                      </div>
                    </div>

                    <div className="pt-8 border-t border-[#e2e8f0]">
                      <h4 className="text-xs font-bold text-[#00255a] uppercase tracking-widest mb-4">
                        Parent / Guardian Details
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12">
                        <InfoField
                          label="Father's Name"
                          value={application.formResponses?.fatherName}
                        />
                        <InfoField
                          label="Father's Phone"
                          value={application.formResponses?.fatherPhone}
                        />
                        <InfoField
                          label="Mother's Name"
                          value={application.formResponses?.motherName}
                        />
                        <InfoField
                          label="Mother's Phone"
                          value={application.formResponses?.motherPhone}
                        />
                      </div>
                    </div>

                    {application.type === "STUDY" && (
                      <div className="pt-8 border-t border-[#e2e8f0]">
                        <h4 className="text-xs font-bold text-[#00255a] uppercase tracking-widest mb-4">
                          Study Preferences
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12">
                          <InfoField
                            label="Target Country"
                            value={application.formResponses?.targetCountry}
                          />
                          <InfoField
                            label="Study Level"
                            value={application.formResponses?.studyLevel}
                          />
                          <InfoField
                            label="Field of Study"
                            value={application.formResponses?.fieldOfStudy}
                          />
                          <InfoField
                            label="Intended Intake"
                            value={application.formResponses?.intendedIntake}
                          />
                          <InfoField
                            label="Highest Qualification"
                            value={
                              application.formResponses?.highestQualification
                            }
                          />
                          <InfoField
                            label="Previous Institution"
                            value={
                              application.formResponses?.previousInstitution
                            }
                          />
                          <InfoField
                            label="English Proficiency"
                            value={
                              application.formResponses?.englishProficiency
                            }
                          />
                          <InfoField
                            label="English Test Score"
                            value={
                              application.formResponses?.englishTestScore
                            }
                          />
                          <InfoField
                            label="Funding Source"
                            value={application.formResponses?.fundingSource}
                          />
                        </div>
                      </div>
                    )}

                    {application.type === "JOB" &&
                      (() => {
                        // JOB assessment form fields aren't hardcoded here
                        // (shape may evolve) — show whatever else is in
                        // formResponses beyond the fields already rendered
                        // above (passport/parent details).
                        const shown = new Set([
                          "passportNumber",
                          "passportExpiry",
                          "fatherName",
                          "fatherPhone",
                          "motherName",
                          "motherPhone",
                        ]);
                        const rest = Object.entries(
                          application.formResponses || {},
                        ).filter(([key]) => !shown.has(key));
                        if (!rest.length) return null;
                        const labelize = (key) =>
                          key
                            .replace(/([a-z])([A-Z])/g, "$1 $2")
                            .replace(/^./, (c) => c.toUpperCase());
                        return (
                          <div className="pt-8 border-t border-[#e2e8f0]">
                            <h4 className="text-xs font-bold text-[#00255a] uppercase tracking-widest mb-4">
                              Job Preferences
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12">
                              {rest.map(([key, value]) => (
                                <InfoField
                                  key={key}
                                  label={labelize(key)}
                                  value={
                                    Array.isArray(value)
                                      ? value.join(", ")
                                      : String(value ?? "")
                                  }
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                  </div>
                )}

                {tab === "documents" && (
                  <div className="space-y-4">
                    {(application.documents || []).length === 0 ? (
                      <p className="text-[#64748b] text-sm">
                        No documents uploaded yet.
                      </p>
                    ) : (
                      application.documents.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-4 bg-[#f7f9fb] rounded-lg border border-[#e2e8f0] group hover:border-[#00255a] transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-16 bg-white border border-[#e2e8f0] rounded flex items-center justify-center">
                              <span className="material-symbols-outlined text-[#64748b]">
                                description
                              </span>
                            </div>
                            <div>
                              <a
                                href={doc.fileUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="font-bold text-[#00255a] hover:underline"
                              >
                                {doc.documentType}
                              </a>
                              <p className="text-[11px] text-[#64748b]">
                                {doc.mimeType} ·{" "}
                                {(doc.fileSizeBytes / 1024 / 1024).toFixed(1)}{" "}
                                MB ·{" "}
                                {new Date(doc.uploadedAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {doc.status === "VERIFIED" ? (
                              <span className="material-symbols-outlined text-green-600 bg-green-50 p-2 rounded-full">
                                check_circle
                              </span>
                            ) : (
                              <button
                                onClick={() =>
                                  handleVerifyDoc(doc.id, "VERIFIED")
                                }
                                className="px-4 py-2 text-sm font-bold text-[#00255a] border border-[#00255a] rounded hover:bg-[#00255a] hover:text-white transition-all"
                              >
                                Verify Now
                              </button>
                            )}
                            <button
                              onClick={() =>
                                handleVerifyDoc(doc.id, "REJECTED")
                              }
                              className="p-2 text-[#64748b] hover:text-red-600 transition-colors"
                            >
                              <span className="material-symbols-outlined">
                                cancel
                              </span>
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {tab === "payments" && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-[#e2e8f0]">
                          {["Description", "Amount", "Status", "Evidence"].map(
                            (h) => (
                              <th
                                key={h}
                                className="pb-4 text-[10px] font-bold text-[#64748b] uppercase tracking-widest"
                              >
                                {h}
                              </th>
                            ),
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#e2e8f0]">
                        {(application.payments || []).length === 0 ? (
                          <tr>
                            <td
                              colSpan="4"
                              className="py-8 text-center text-[#64748b]"
                            >
                              No payments logged.
                            </td>
                          </tr>
                        ) : (
                          application.payments.map((p) => (
                            <tr
                              key={p.id}
                              className="hover:bg-[#f7f9fb] transition-colors"
                            >
                              <td className="py-4 font-bold text-[#00255a]">
                                {p.label}
                              </td>
                              <td className="py-4 text-[#00255a]">
                                {p.currency} {Number(p.amount).toLocaleString()}
                              </td>
                              <td className="py-4">
                                <span
                                  className={`px-2 py-1 text-[11px] font-bold rounded uppercase ${
                                    p.status === "CONFIRMED"
                                      ? "bg-green-50 text-green-700"
                                      : "bg-amber-50 text-amber-700"
                                  }`}
                                >
                                  {p.status === "CONFIRMED"
                                    ? "Paid"
                                    : p.status.replace("_", " ")}
                                </span>
                              </td>
                              <td className="py-4 text-right">
                                {p.proofUrl && (
                                  <a
                                    href={p.proofUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-[#00255a] hover:underline font-bold text-sm flex items-center justify-end"
                                  >
                                    <span className="material-symbols-outlined text-[18px] mr-1">
                                      receipt_long
                                    </span>
                                    View Receipt
                                  </a>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-xl shadow-[0px_10px_30px_rgba(30,60,114,0.05)] border border-[#e2e8f0] p-8">
              <h3 className="text-xl font-bold text-[#00255a] mb-6">
                Application Timeline
              </h3>
              <div className="space-y-8">
                {(application.statusLogs || [])
                  .slice()
                  .reverse()
                  .map((log, i) => (
                    <div key={log.id} className="relative flex gap-6 pl-2">
                      <div className="z-10 w-6 h-6 rounded-full bg-[#365da3] text-white flex items-center justify-center ring-4 ring-[#365da3]/10 flex-shrink-0">
                        <span className="material-symbols-outlined text-[14px]">
                          done
                        </span>
                      </div>
                      <div className="flex-1 -mt-1 pb-2">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <p className="font-bold text-[#00255a]">
                            {stageLabel(log.toStage)}
                          </p>
                          <span className="text-sm text-[#64748b]">
                            {new Date(log.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-[#64748b] mt-1">
                          {log.note ||
                            `Moved by ${log.changedBy?.fullName || "system"}`}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            {!isClosed && (
              <div className="bg-white rounded-xl shadow-[0px_10px_30px_rgba(30,60,114,0.05)] border border-[#e2e8f0] p-6">
                <h3 className="text-xs font-bold text-[#00255a] uppercase tracking-widest mb-4">
                  Advance Application
                </h3>
                <label className="text-[11px] font-bold text-[#64748b] block mb-2">
                  Select Next Stage
                </label>
                <select
                  value={nextStage}
                  onChange={(e) => setNextStage(e.target.value)}
                  className="w-full bg-[#f7f9fb] border border-[#e2e8f0] rounded-lg px-4 py-3 font-bold text-[#00255a] outline-none focus:ring-2 focus:ring-[#00255a]/10 mb-4"
                >
                  {STAGE_ORDER.map((s) => (
                    <option key={s} value={s}>
                      {stageLabel(s)}
                    </option>
                  ))}
                </select>
                <div className="p-4 bg-[#1e3c72]/5 border border-[#1e3c72]/10 rounded-lg mb-4">
                  <div className="flex gap-3">
                    <span className="material-symbols-outlined text-[#1e3c72]">
                      info
                    </span>
                    <p className="text-xs text-[#64748b]">
                      Next stage:{" "}
                      <strong className="text-[#00255a]">
                        {stageLabel(nextStage)}
                      </strong>
                      . The applicant will be notified.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleStageUpdate}
                  className="w-full py-3 bg-[#00255a] text-white font-bold rounded-lg hover:opacity-90 transition-all shadow-md"
                >
                  Update Application Stage
                </button>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-[0px_10px_30px_rgba(30,60,114,0.05)] border border-[#e2e8f0] p-6">
              <h3 className="text-xs font-bold text-[#00255a] uppercase tracking-widest mb-4">
                Assigned Agent
              </h3>
              {application.assignedAgent ? (
                <div className="flex items-center gap-4 p-3 bg-[#f7f9fb] rounded-lg">
                  <div className="w-12 h-12 rounded-full bg-[#8fb3ff] flex items-center justify-center text-white font-bold">
                    {application.assignedAgent.fullName?.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold text-[#00255a]">
                      {application.assignedAgent.fullName}
                    </p>
                    <p className="text-sm text-[#64748b]">
                      {application.assignedAgent.email}
                    </p>
                  </div>
                  <a
                    href={`https://wa.me/${application.assignedAgent.whatsapp?.replace(/\D/g, "")}`}
                    className="ml-auto p-2 text-[#64748b] hover:text-[#00255a]"
                  >
                    <span className="material-symbols-outlined">chat</span>
                  </a>
                </div>
              ) : (
                <p className="text-[#64748b] text-sm">No agent assigned yet.</p>
              )}
            </div>
          </div>
        </div>

        {/* Reassign Modal */}
        {reassignOpen && (
          <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
            onClick={() => setReassignOpen(false)}
          >
            <div
              className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-[#00255a] mb-4">
                Reassign Agent
              </h3>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {agents.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => handleReassign(a.id)}
                    className="w-full text-left px-4 py-3 rounded-lg hover:bg-[#f2f4f6] flex items-center gap-3"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#8fb3ff] flex items-center justify-center text-white text-xs font-bold">
                      {a.fullName?.charAt(0)}
                    </div>
                    <span className="text-sm font-semibold text-[#0f172a]">
                      {a.fullName}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Reject Modal */}
        {rejectOpen && (
          <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
            onClick={() => setRejectOpen(false)}
          >
            <div
              className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-[#00255a] mb-4">
                Reject Application
              </h3>
              <textarea
                rows="4"
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="Reason for rejection..."
                className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] mb-4 resize-none outline-none focus:ring-2 focus:ring-[#00255a]/10"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setRejectOpen(false)}
                  className="flex-1 py-3 border border-[#e2e8f0] rounded-lg font-semibold text-[#44474f]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  className="flex-1 py-3 bg-red-600 text-white rounded-lg font-semibold"
                >
                  Confirm Reject
                </button>
              </div>
            </div>
          </div>
        )}

        <a
          href={`https://wa.me/${application.applicant?.whatsapp?.replace(/\D/g, "")}`}
          className="fixed bottom-8 right-8 w-14 h-14 bg-[#25D366] text-white rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-transform group"
        >
          <span className="material-symbols-outlined text-[28px]">forum</span>
          <span className="absolute right-16 bg-white px-3 py-1 rounded shadow-lg text-[#00255a] text-sm font-bold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
            WhatsApp Applicant
          </span>
        </a>
      </main>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] font-bold text-[#64748b] uppercase tracking-widest">
        {label}
      </label>
      <p className="font-bold text-[#00255a]">{value || "—"}</p>
    </div>
  );
}